<template>
  <div class="w-full">
    <label
      v-if="label"
      :for="selectId"
      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
    >
      {{ label }}
      <span v-if="required" class="text-red-500 dark:text-red-400">*</span>
    </label>
    
    <div class="relative">
      <select
        :id="selectId"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :class="selectClasses"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      >
        <option v-if="placeholder" value="" disabled>
          {{ placeholder }}
        </option>
        <option
          v-for="option in options"
          :key="getOptionValue(option)"
          :value="getOptionValue(option)"
        >
          {{ getOptionLabel(option) }}
        </option>
      </select>
      
      <!-- Icon -->
      <div
        v-if="icon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <component :is="icon" class="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>
      
      <!-- Dropdown arrow -->
      <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
        <svg class="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        </svg>
      </div>
    </div>
    
    <!-- Error message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>
    
    <!-- Help text -->
    <p v-if="help && !error" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
      {{ help }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// Props
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    required: true
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  help: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  icon: {
    type: [String, Object],
    default: null
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  displayLabelKey: {
    type: String,
    default: null
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'change', 'blur', 'focus'])

// Reactive data
const selectId = ref(`select-${Math.random().toString(36).substr(2, 9)}`)

// Computed
const selectClasses = computed(() => {
  const baseClasses = 'block w-full rounded-md shadow-sm sm:text-sm appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
  
  const stateClasses = props.error
    ? 'border-red-300 dark:border-red-500 focus:border-red-500 focus:ring-red-500'
    : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500'
  
  const paddingClasses = props.icon
    ? 'pl-10 pr-10'
    : 'pl-3 pr-10'
  
  const disabledClasses = props.disabled
    ? 'bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed'
    : ''
  
  return [
    baseClasses,
    stateClasses,
    paddingClasses,
    disabledClasses
  ].filter(Boolean).join(' ')
})

// Methods
const getOptionValue = (option) => {
  if (typeof option === 'string') return option
  return option[props.valueKey] || option.value
}

const getOptionLabel = (option) => {
  if (typeof option === 'string') return option
  
  // Use displayLabelKey if provided, otherwise fall back to labelKey
  const labelKey = props.displayLabelKey || props.labelKey
  return option[labelKey] || option.label || option.name
}

const handleChange = (event) => {
  const value = event.target.value
  emit('update:modelValue', value)
  emit('change', value)
}

const handleBlur = (event) => {
  emit('blur', event)
}

const handleFocus = (event) => {
  emit('focus', event)
}
</script>
